// ContactForm.jsx
import { useRef, useState, useEffect } from "react";
import gsap from "gsap";
import * as StyledContactForm from "./Styled.ContactForm";

const ContactForm = () => {
  // Inside ContactForm.jsx
  const [selectedPlan, setSelectedPlan] = useState("");
  const formRef = useRef(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const planRef = useRef(null);
  const messageRef = useRef(null);

  const timelinesRef = useRef(new Map());
  const listenersRef = useRef([]);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(""); // "", "success", "error"
  const [errorMessage, setErrorMessage] = useState(""); // new inline error
  const [startedAt] = useState(Date.now());

  const endpoint =
    process.env.NODE_ENV === "development"
      ? "/api/contact"
      : process.env.REACT_APP_WORKER_URL ||
        "https://worker-contact.hello-4b3.workers.dev";

  // GSAP animations
  useEffect(() => {
    if (!formRef.current) return;

    const ctx = gsap.context(() => {
      const inputs = [
        nameRef.current,
        emailRef.current,
        phoneRef.current,
        messageRef.current,
        planRef.current,
      ];

      inputs.forEach((inputEl) => {
        if (!inputEl) return;
        const labelEl = inputEl.parentElement?.querySelector("label");
        const textEl = inputEl.parentElement?.querySelector(".text");
        if (!labelEl || !textEl) return;

        const tl = gsap.timeline({ paused: true });

        tl.to(labelEl, {
          duration: 0.15,
          scale: 0.75,
          yPercent: -50,
          x: -24,
          ease: "power3.inOut",
        });

        tl.to(
          textEl,
          {
            duration: 0.15,
            color: "#ED2835",
            padding: "0 8px",
            ease: "power3.inOut",
          },
          0,
        );

        timelinesRef.current.set(inputEl, tl);

        if (inputEl.value && inputEl.value.trim().length > 0) {
          tl.progress(1);
        }

        const onFocus = () => tl.play();
        const onBlur = () => {
          if (!inputEl.value || inputEl.value.trim().length === 0) {
            tl.reverse();
          }
        };
        // Added 'onChange' specifically for the Select dropdown
        const onChange = () => {
          if (inputEl.value && inputEl.value.trim().length > 0) {
            tl.play();
          } else {
            tl.reverse();
          }
        };

        inputEl.addEventListener("focus", onFocus);
        inputEl.addEventListener("blur", onBlur);
        inputEl.addEventListener("change", onChange);
        listenersRef.current.push({ inputEl, onFocus, onBlur, onChange });
      });
    }, formRef.current);

    return () => {
      listenersRef.current.forEach(({ inputEl, onFocus, onBlur, onChange }) => {
        try {
          inputEl.removeEventListener("focus", onFocus);
          inputEl.removeEventListener("blur", onBlur);
          inputEl.removeEventListener("change", onChange);
        } catch {}
      });
      listenersRef.current = [];
      timelinesRef.current.clear();
      ctx.revert();
    };
  }, []);

  useEffect(() => {
    const planEl = planRef.current;
    if (!planEl) return;

    const tl = timelinesRef.current.get(planEl);
    if (!tl) return;

    if (selectedPlan && selectedPlan !== "") {
      tl.play(); // Move label up when plan is auto-selected
    } else {
      tl.reverse(); // Move label down if cleared
    }
  }, [selectedPlan]); // This watches for the button clicks/hash changes

  // simple validation returning message
  const validateFields = () => {
    const name = nameRef.current?.value?.trim() || "";
    const email = emailRef.current?.value?.trim() || "";
    const phone = phoneRef.current?.value?.trim() || "";
    const message = messageRef.current?.value?.trim() || "";
    const plan = selectedPlan;

    if (!name) return { ok: false, message: "Please enter your name." };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return { ok: false, message: "Please enter a valid email." };
    if (!phone)
      return { ok: false, message: "Please enter your contact number." };
    if (!plan)
      return {
        ok: false,
        message: "Please select a plan you are interested in.",
      };
    if (message.length < 3)
      return { ok: false, message: "Please enter project description." };

    return { ok: true, data: { name, email, phone, message, plan } };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setErrorMessage("");

    const validation = validateFields();
    if (!validation.ok) {
      setErrorMessage(validation.message); // show error inline
      return;
    }

    setLoading(true);

    const formEl = formRef.current;
    const payload = {
      ...validation.data,
      // plan: selectedPlan, // Add this line
      website: formEl?.website?.value || "",
      startedAt,
    };

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {}

      if (res.ok && data.ok !== false) {
        setStatus("success");
        formEl.reset();
        timelinesRef.current.forEach((tl, inputEl) => {
          if (!inputEl.value || inputEl.value.trim().length === 0) {
            tl.reverse(0);
          }
        });
      } else {
        setStatus("error");
        setErrorMessage("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("ContactForm error:", err);
      setStatus("error");
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Function to grab plan from URL on page load
    const checkUrl = () => {
      const hash = window.location.hash;
      if (hash.includes("?plan=")) {
        setSelectedPlan(hash.split("?plan=")[1]);
      }
    };

    // Function to handle the custom event from the button click
    const handleCustomEvent = (e) => {
      setSelectedPlan(e.detail);
    };

    // Initial check
    checkUrl();

    // Listeners
    window.addEventListener("hashchange", checkUrl);
    window.addEventListener("planSelect", handleCustomEvent);

    return () => {
      window.removeEventListener("hashchange", checkUrl);
      window.removeEventListener("planSelect", handleCustomEvent);
    };
  }, []);

  return (
    <StyledContactForm.GridFormWrapper>
      <StyledContactForm.GridItemFormWrapper>
        <StyledContactForm.FormCard>
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            id="contactForm"
            noValidate
          >
            <StyledContactForm.ModulePin as="legend">
              Fill up the form
            </StyledContactForm.ModulePin>

            <StyledContactForm.GridForm>
              {/* title */}
              <StyledContactForm.GridItemForm>
                <StyledContactForm.FormTitle>
                  Let’s make something happen
                </StyledContactForm.FormTitle>
              </StyledContactForm.GridItemForm>

              {/* description */}
              <StyledContactForm.GridItemForm>
                <p>
                  I can’t read minds (yet), so fill out the form below. The more
                  details you give, the faster we can turn your idea into a
                  WordPress website or brand identity that actually works.
                </p>
              </StyledContactForm.GridItemForm>

              {/* name */}
              <StyledContactForm.GridItemForm>
                <StyledContactForm.TextFieldWrapper>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    aria-labelledby="label-name"
                    ref={nameRef}
                    required
                  />
                  <label className="label" htmlFor="name" id="label-name">
                    <StyledContactForm.FormInputText className="text">
                      Your Name
                    </StyledContactForm.FormInputText>
                  </label>
                </StyledContactForm.TextFieldWrapper>
              </StyledContactForm.GridItemForm>

              {/* email */}
              <StyledContactForm.GridItemForm>
                <StyledContactForm.TextFieldWrapper>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    aria-labelledby="label-email"
                    ref={emailRef}
                    required
                  />
                  <label className="label" htmlFor="email" id="label-email">
                    <StyledContactForm.FormInputText className="text">
                      Your Email
                    </StyledContactForm.FormInputText>
                  </label>
                </StyledContactForm.TextFieldWrapper>
              </StyledContactForm.GridItemForm>

              {/* Phone */}
              <StyledContactForm.GridItemForm>
                <StyledContactForm.TextFieldWrapper>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    ref={phoneRef}
                    placeholder=" " // Keep space for label float logic
                    required
                  />
                  <label className="label" htmlFor="phone">
                    <StyledContactForm.FormInputText className="text">
                      Contact Number (e.g. +65...)
                    </StyledContactForm.FormInputText>
                  </label>
                </StyledContactForm.TextFieldWrapper>
              </StyledContactForm.GridItemForm>

              {/* Select Plan Dropdown */}
              <StyledContactForm.GridItemForm>
                <StyledContactForm.TextFieldWrapper>
                  <select
                    id="plan"
                    name="plan"
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    ref={planRef}
                    required
                  >
                    <option value="" disabled>
                      Select a plan
                    </option>
                    <option value="foundation">The Foundation Build</option>
                    <option value="partnership">The Growth Partnership</option>
                    <option value="fractional">The Fractional Partner</option>
                    {/* The new premium 'other' option */}
                    <option value="custom">The Bespoke Brief</option>
                  </select>
                  {/* Label stays active if a value is selected */}
                  <label className="label" htmlFor="plan">
                    <StyledContactForm.FormInputText className="text">
                      Interested In
                    </StyledContactForm.FormInputText>
                  </label>
                </StyledContactForm.TextFieldWrapper>
              </StyledContactForm.GridItemForm>

              {/* message */}
              <StyledContactForm.GridItemForm>
                <StyledContactForm.TextFieldWrapper>
                  <textarea
                    id="message"
                    name="message"
                    aria-labelledby="label-message"
                    ref={messageRef}
                    rows={5}
                    required
                  />
                  <label className="label" htmlFor="message" id="label-message">
                    <StyledContactForm.FormInputText className="text">
                      Project Description
                    </StyledContactForm.FormInputText>
                  </label>
                </StyledContactForm.TextFieldWrapper>
              </StyledContactForm.GridItemForm>

              {/* HONEYPOT + startedAt */}
              <input
                type="text"
                name="website"
                autoComplete="off"
                tabIndex={-1}
                style={{ position: "absolute", left: "-9999px", opacity: 0 }}
              />
              <input type="hidden" name="startedAt" value={startedAt} />

              {/* submit button */}
              <StyledContactForm.GridItemForm>
                <StyledContactForm.FormButton
                  type="submit"
                  as="button"
                  disabled={loading || status === "success"}
                >
                  {loading
                    ? "Sending…"
                    : status === "success"
                      ? "Sent"
                      : "Send"}
                </StyledContactForm.FormButton>
              </StyledContactForm.GridItemForm>

              {/* inline error */}
              {errorMessage && (
                <StyledContactForm.GridItemForm>
                  <StyledContactForm.FormMessageStyle>
                    {errorMessage}
                  </StyledContactForm.FormMessageStyle>
                </StyledContactForm.GridItemForm>
              )}

              {/* success message */}
              {status === "success" && !errorMessage && (
                <StyledContactForm.GridItemForm>
                  <StyledContactForm.FormMessageStyle success>
                    Thanks! Your message was sent.
                  </StyledContactForm.FormMessageStyle>
                </StyledContactForm.GridItemForm>
              )}
            </StyledContactForm.GridForm>
          </form>
        </StyledContactForm.FormCard>
      </StyledContactForm.GridItemFormWrapper>

      <StyledContactForm.GridItemFormWrapper>
        <small>
          Expect a reply within a few business days. All your info stays
          private.
        </small>
      </StyledContactForm.GridItemFormWrapper>
    </StyledContactForm.GridFormWrapper>
  );
};

export default ContactForm;
